// =============================================================================
// FILE: packages/renderer/src/stores/redAlert.store.ts
// PURPOSE: R2 Red Alert Mode — global calm-attentive voice/UI state.
//          Never panicked. Never robotic. Reassurance, not anxiety.
// =============================================================================

import { useSyncExternalStore } from 'react';

export type AlertMode = 'normal' | 'red-alert';

interface RedAlertState {
  mode:       AlertMode;
  activatedAt: number | null;
  activatedBy: 'voice' | 'keyboard' | 'button' | null;
}

const subscribers = new Set<() => void>();
let state: RedAlertState = { mode: 'normal', activatedAt: null, activatedBy: null };

function notify(): void { for (const s of subscribers) s(); }

// Activation voice triggers
export const RED_ALERT_TRIGGERS   = ['red alert', 'red alert two', 'r2 alert', 'red alert mode'];
export const STAND_DOWN_TRIGGERS  = ['normal mode', 'stand down', 'resume normal operations', 'cancel alert', 'all clear'];

// Red Alert personality: wraps neutral text with calm, attentive phrasing
export function applyRedAlertPersonality(text: string, userName = 'John'): string {
  const t = text.trim();
  // Short notifications get prefixed with the user's name + empathetic framing
  if (t.length < 120) {
    return `${userName}, ${t.charAt(0).toLowerCase() + t.slice(1)} I am right here with you.`;
  }
  // Longer text: gentle opener and reassuring close
  return `${userName}, I want to make sure you have this: ${t} Take it one step at a time — I am with you.`;
}

// Normal personality — no transformation
export function applyNormalPersonality(text: string): string {
  return text;
}

export const redAlertStore = {
  getState: (): RedAlertState => state,

  activate(by: RedAlertState['activatedBy'] = 'button'): void {
    state = { mode: 'red-alert', activatedAt: Date.now(), activatedBy: by };
    notify();
  },

  deactivate(): void {
    state = { mode: 'normal', activatedAt: null, activatedBy: null };
    notify();
  },

  toggle(by: RedAlertState['activatedBy'] = 'button'): void {
    if (state.mode === 'red-alert') redAlertStore.deactivate();
    else redAlertStore.activate(by);
  },

  subscribe(fn: () => void): () => void {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },

  isActive(): boolean { return state.mode === 'red-alert'; },
};

export function useRedAlertStore(): RedAlertState {
  return useSyncExternalStore(
    redAlertStore.subscribe,
    redAlertStore.getState,
    redAlertStore.getState,
  );
}
